import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { AuthUser } from '../types/AuthUser';
import {
  getInitialAuthUser,
  signInWithEmailPasswordCredentials,
  signInWithGoogleOAuth,
  signInWithMagicLinkOtp,
  signOutRobustly,
  signUpWithEmailPasswordCredentials,
  subscribeSupabaseAuth,
} from '../services/supabaseAuthSession';

/** Показать на /auth форму входа email+password (через Supabase Auth). */
export function isEmailPasswordLoginEnabled(): boolean {
  const v = import.meta.env.VITE_ENABLE_EMAIL_PASSWORD_LOGIN;
  return v === 'true' || v === '1';
}

function buildAuthRedirectUrl(): string {
  const configuredPath = import.meta.env.VITE_AUTH_REDIRECT_PATH?.trim();
  const redirectPath =
    configuredPath && configuredPath.startsWith('/') ? configuredPath : '/auth';
  return `${window.location.origin}${redirectPath}`;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signOutUser: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<void>;
  signInWithEmailPassword: (email: string, password: string) => Promise<void>;
  signUpWithEmailPassword: (email: string, password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const initialUser = await getInitialAuthUser();
        if (!cancelled) {
          setUser(initialUser);
          setLoading(false);
        }
      } catch (e) {
        console.error('Supabase session init error:', e);
        if (!cancelled) setLoading(false);
      }
    })();

    const { unsubscribe } = subscribeSupabaseAuth((authUser) => {
      setUser(authUser);
      setLoading(false);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const signOutUser = async () => {
    await signOutRobustly();
  };

  const signInWithGoogle = async () => {
    await signInWithGoogleOAuth(buildAuthRedirectUrl());
  };

  const signInWithMagicLink = async (email: string) => {
    await signInWithMagicLinkOtp(email, buildAuthRedirectUrl());
  };

  const signInWithEmailPassword = async (email: string, password: string) => {
    await signInWithEmailPasswordCredentials(email, password);
  };

  const signUpWithEmailPassword = async (email: string, password: string) => {
    await signUpWithEmailPasswordCredentials(email, password, buildAuthRedirectUrl());
  };

  const value: AuthContextValue = {
    user,
    loading,
    signOutUser,
    signInWithGoogle,
    signInWithMagicLink,
    signInWithEmailPassword,
    signUpWithEmailPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
